import {
  Alert,
  Box,
  Button,
  CardContent,
  CardHeader,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import React, { useEffect, useState, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { StyledCard } from "../StepMainEdit";
import { VendorCardTitle, VendorListHolder } from "./vendorReg";
import BankDetailsContent1 from "../../../bankDetails/components/bankDetailsContent1";
import {
  getBanks,
  removeBank,
} from "../../../bankDetails/redux/bankDetailsSlice";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import {
  LeftDetailsBankCard,
  RightDetailsBankCard,
} from "../../../bankDetails/components/bankAccountsContent";
import LoadingButton from "@mui/lab/LoadingButton";
import { message } from "antd";
import {
  submitVendorEdit,
  getEffectiveLocations,
} from "../../redux/vendorRegistrationSlice";
import LocalStorage, { SESSION_KEY } from "../../../../utilities/localStorage";
import { useFeature } from "../../../../common/featureFlags/FeatureProvider";

const BankCard = ({ item, is_domestic, onEdit, onDelete, isLocked }) => {
  const bankData = item?.item || item;
  const {
    id,
    bank_name,
    city,
    branch_name,
    account_name,
    account_number,
    is_primary_account,
    currency,
    locations,
  } = bankData;

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <StyledCard sx={{ border: "1px solid #EAEAEF", mb: 2 }}>
      <CardHeader
        action={
          !isLocked && (
            <IconButton aria-label="settings" onClick={handleClick}>
              <MoreVertIcon />
            </IconButton>
          )
        }
        title={
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: "16px" }}>
              {bank_name}
            </Typography>
            {is_primary_account ? (
              <Box
                sx={{
                  fontSize: "12px",
                  borderRadius: "15px",
                  background: "#D55EE1",
                  padding: "2px 12px",
                  color: "white",
                  ml: 1,
                }}
              >
                Primary
              </Box>
            ) : null}
          </Box>
        }
      />
      <Menu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {!isLocked && (
          <>
            <MenuItem
              onClick={() => {
                handleClose();
                onEdit(id);
              }}
            >
              Edit
            </MenuItem>
            <MenuItem
              onClick={() => {
                handleClose();
                if (onDelete) onDelete(id, bankData);
              }}
              sx={{ color: "error.main" }}
            >
              Delete
            </MenuItem>
          </>
        )}
      </Menu>
      <CardContent>
        <Grid container spacing={1}>
          <Grid item xs={6}>
            <LeftDetailsBankCard>City:</LeftDetailsBankCard>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <RightDetailsBankCard>{city}</RightDetailsBankCard>
          </Grid>
          <Grid item xs={6}>
            <LeftDetailsBankCard>Branch:</LeftDetailsBankCard>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <RightDetailsBankCard>{branch_name}</RightDetailsBankCard>
          </Grid>
          <Grid item xs={6}>
            <LeftDetailsBankCard>A/C name:</LeftDetailsBankCard>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <RightDetailsBankCard>{account_name}</RightDetailsBankCard>
          </Grid>
          <Grid item xs={6}>
            <LeftDetailsBankCard>
              {is_domestic ? "A/C number:" : "IBAN Number"}
            </LeftDetailsBankCard>
          </Grid>
          <Grid item xs={6} sx={{ textAlign: "right" }}>
            <RightDetailsBankCard>{account_number}</RightDetailsBankCard>
          </Grid>
          {locations && locations.length > 0 && (
            <Grid item xs={12} sx={{ mt: 1 }}>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", alignItems: "center" }}>
                <Typography variant="caption" sx={{ color: "#666", fontWeight: 600 }}>
                  Assigned Locations:
                </Typography>
                {locations.map((loc) => (
                  <Box
                    key={loc.id}
                    sx={{
                      fontSize: "11px",
                      background: "#e0e7ff",
                      color: "#3730a3",
                      padding: "2px 8px",
                      borderRadius: "12px",
                      fontWeight: 500,
                    }}
                  >
                    📍 {loc.location_code ? `[${loc.location_code}] ` : ""}{loc.location}
                  </Box>
                ))}
              </Box>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </StyledCard>
  );
};

export default function BankDetailsManager({
  vendor_id,
  user_id,
  handleBack,
  activeStep,
  is_domestic,
  company_id,
  setSkipped,
  setActiveStep,
  isStepSkipped,
  skipped,
  isLocked,
  isCreation = false,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const loginUserDetails = LocalStorage.getItem(SESSION_KEY);
  const { banks, loading } = useSelector((state) => state.bankdetails);
  const { createdUser } = useSelector((state) => state.vendorRegistration);
  const [view, setView] = useState("list"); // list, add, edit
  const [effectiveLocations, setEffectiveLocations] = useState([]);
  const [editingBankId, setEditingBankId] = useState(null);
  const [hasPendingChanges, setHasPendingChanges] = useState(false);
  const [isFinishing, setIsFinishing] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingBank, setDeletingBank] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const vendor_approval_workflow = useFeature("vendor_approval_workflow");
  const isVendorEditWorkflow = useFeature("vendor_edit_workflow");
  const isAutoApproveVendor = useFeature("auto_approve_vendor");
  const showRequestedDocs = useFeature("requested_documents");
  const disableMultipleBanks = useFeature("disable_multiple_banks");

  const hasWorkflow =
    isVendorEditWorkflow || isAutoApproveVendor || vendor_approval_workflow;
  const shouldFinish = vendor_approval_workflow || isAutoApproveVendor;
  const isOfficer = loginUserDetails.roles.includes("officer");
  // eslint-disable-next-line no-console
  console.log("isLocked", isLocked);

  const currentVendorId =
    vendor_id ||
    createdUser?.vendor_id ||
    loginUserDetails?.vendor_id ||
    loginUserDetails?.id;

  const unmappedLocations = useMemo(() => {
    if (!effectiveLocations || effectiveLocations.length === 0) return [];
    return effectiveLocations.filter((loc) => {
      const hasBank = Boolean(
        loc.bank_id ||
          loc.bank_detail?.id ||
          loc.BankInformationId ||
          loc.bank_information_id,
      );
      return !hasBank;
    });
  }, [effectiveLocations]);

  const hasPendingUnmappedLocations = unmappedLocations.length > 0;

  const handleFinish = () => {
    if (!currentVendorId) {
      message.error(
        "Vendor ID is missing. Please refresh the page and try again.",
      );
      return;
    }

    if (isCreation && !hasWorkflow) {
      message.success("Registration completed successfully!");
      navigate("/app");
      return;
    }

    const payload = {
      vendor_id: currentVendorId,
      is_finish: shouldFinish ? true : false,
    };

    setIsFinishing(true);
    dispatch(submitVendorEdit(payload))
      .unwrap()
      .then((res) => {
        setIsFinishing(false);
        if (res?.success) {
          message.success(res?.message || "Edits submitted successfully.");
        } else {
          message.info(res?.message || "No pending edits to submit.");
        }
        navigate("/app");
      })
      .catch((err) => {
        setIsFinishing(false);
        message.error(
          err?.message || err?.errorMessage || "Failed to submit edits.",
        );
      });
  };

  const handleProceed = () => {
    if (hasPendingUnmappedLocations) {
      const locNames = unmappedLocations
        .map((l) => l.location || l.city || `Location #${l.id}`)
        .join(", ");
      message.error(
        `Please add/map bank details for location(s): ${locNames} before proceeding.`,
      );
      return;
    }

    if (showRequestedDocs) {
      let newSkipped = skipped;
      if (isStepSkipped && isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      if (setActiveStep) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      if (setSkipped) setSkipped(newSkipped);
    } else {
      handleFinish();
    }
  };

  useEffect(() => {
    if (currentVendorId || user_id) {
      dispatch(getBanks(currentVendorId || user_id));
      dispatch(getEffectiveLocations(currentVendorId || user_id))
        .then((res) => {
          const locList = res?.payload?.data || res?.data?.data || [];
          setEffectiveLocations(locList);
        })
        .catch((err) => {
          console.error(
            "Error fetching effective locations in BankDetailsManager: ",
            err,
          );
        });
    }
  }, [dispatch, currentVendorId, user_id]);

  const handleAdd = () => {
    setView("add");
  };

  const handleEdit = (id) => {
    setEditingBankId(id);
    setView("edit");
  };

  const handleDelete = (id, bankData) => {
    setDeletingBank({ id, bankData });
    setDeleteDialogOpen(true);
  };

  const handleCloseDeleteDialog = () => {
    if (!isDeleting) {
      setDeleteDialogOpen(false);
      setDeletingBank(null);
    }
  };

  const handleConfirmDelete = () => {
    if (!deletingBank?.id) return;
    setIsDeleting(true);
    dispatch(
      removeBank({
        bank_id: deletingBank.id,
        vid: currentVendorId,
        reason: "Deleted from registration",
      })
    )
      .unwrap()
      .then((res) => {
        setIsDeleting(false);
        setDeleteDialogOpen(false);
        setDeletingBank(null);
        if (res?.success) {
          message.success(res?.message || "Bank details deleted successfully");
        }
        dispatch(getBanks(currentVendorId || user_id));
        dispatch(getEffectiveLocations(currentVendorId || user_id)).then(
          (locRes) => {
            const locList = locRes?.payload?.data || locRes?.data?.data || [];
            setEffectiveLocations(locList);
          }
        );
        setHasPendingChanges(true);
      })
      .catch((err) => {
        setIsDeleting(false);
        message.error(
          err?.message || err?.errorMessage || "Failed to delete bank details"
        );
      });
  };

  const handleCancel = () => {
    setView("list");
    setEditingBankId(null);
  };

  const handleSuccess = () => {
    if (disableMultipleBanks) {
      let newSkipped = skipped;
      if (isStepSkipped && isStepSkipped(activeStep)) {
        newSkipped = new Set(newSkipped.values());
        newSkipped.delete(activeStep);
      }
      if (setActiveStep) {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
      }
      if (setSkipped) {
        setSkipped(newSkipped);
      }
    } else {
      setView("list");
      setEditingBankId(null);
      dispatch(getBanks(currentVendorId || user_id));
      dispatch(getEffectiveLocations(currentVendorId || user_id)).then(
        (res) => {
          const locList = res?.payload?.data || res?.data?.data || [];
          setEffectiveLocations(locList);
        },
      );
      setHasPendingChanges(true);
    }
  };

  if (view === "add" || view === "edit") {
    return (
      <BankDetailsContent1
        vendor_id={currentVendorId}
        createdUser={{ id: user_id }}
        is_domestic={is_domestic}
        company_id={company_id}
        bankInfoId={editingBankId}
        isVendorEditStepper={true}
        onSuccess={handleSuccess}
        onCancel={handleCancel}
        isLocked={isLocked}
        effectiveLocations={effectiveLocations}
      />
    );
  }

  return (
    <Box>
      <StyledCard>
        <CardContent>
          <VendorCardTitle>
            {isOfficer ? "Step 4" : "Step 3"}: Bank Details
          </VendorCardTitle>
          <VendorListHolder>
            <li>
              Manage your bank account here.{" "}
              {vendor_approval_workflow
                ? "Only one bank account is allowed."
                : ""}
            </li>
            <li>Ensure the account is marked as Primary.</li>
          </VendorListHolder>
        </CardContent>
      </StyledCard>

      {hasPendingUnmappedLocations && (
        <Box sx={{ my: 2 }}>
          <Alert severity="warning">
            The following location(s) do not have a bank account mapped:{" "}
            <strong>
              {unmappedLocations
                .map((l) => l.location || l.city || `Location #${l.id}`)
                .join(", ")}
            </strong>
            . Please add or edit a bank account and assign these location(s) before proceeding.
          </Alert>
        </Box>
      )}

      <Grid container spacing={2}>
        {banks && banks.length > 0 ? (
          banks.map((bank) => (
            <Grid item xs={12} md={6} key={bank.id}>
              <BankCard
                item={bank}
                is_domestic={is_domestic}
                onEdit={handleEdit}
                onDelete={handleDelete}
                isLocked={isLocked}
              />
            </Grid>
          ))
        ) : (
          <Grid item xs={12}>
            <Typography sx={{ textAlign: "center", my: 4, color: "#666687" }}>
              No bank accounts added yet.
            </Typography>
          </Grid>
        )}
      </Grid>

      {!isLocked && (!disableMultipleBanks || !banks || banks.length === 0) && (
        <Box sx={{ textAlign: "center", my: 2 }}>
          <Button
            variant="outlined"
            onClick={handleAdd}
            sx={{ textTransform: "none", borderStyle: "dashed" }}
          >
            + Add Bank Account
          </Button>
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          pt: 2,
          justifyContent: "space-between",
        }}
      >
        <Button color="inherit" onClick={handleBack} sx={{ mr: 1 }}>
          Back
        </Button>
        <Tooltip
          title={
            !banks || banks.length === 0
              ? "Please add at least one bank account to continue"
              : hasPendingUnmappedLocations
                ? `Please map bank details for location(s): ${unmappedLocations
                    .map((l) => l.location || l.city || `Location #${l.id}`)
                    .join(", ")}`
                : ""
          }
          arrow
          placement="top"
        >
          <span>
            <LoadingButton
              sx={{
                textTransform: "none",
                background: "var(--button-color)",
                width: "200px",
              }}
              color="success"
              variant="contained"
              onClick={handleProceed}
              disabled={
                isLocked ||
                !banks ||
                banks.length === 0 ||
                hasPendingUnmappedLocations ||
                isFinishing
              }
              loading={isFinishing}
              loadingPosition="start"
            >
              {showRequestedDocs ? "Next" : isLocked ? "Submitted" : "Finish"}
            </LoadingButton>
          </span>
        </Tooltip>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        maxWidth="xs"
        fullWidth
        aria-labelledby="delete-bank-dialog-title"
        aria-describedby="delete-bank-dialog-description"
      >
        <DialogTitle id="delete-bank-dialog-title" sx={{ fontWeight: 600 }}>
          Delete Bank Account
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-bank-dialog-description">
            Are you sure you want to delete{" "}
            <strong>
              {deletingBank?.bankData?.bank_name || "this bank account"}
            </strong>
            {deletingBank?.bankData?.account_number
              ? ` (${deletingBank.bankData.account_number})`
              : ""}
            ?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button
            onClick={handleCloseDeleteDialog}
            color="inherit"
            disabled={isDeleting}
            sx={{ textTransform: "none" }}
          >
            Cancel
          </Button>
          <LoadingButton
            onClick={handleConfirmDelete}
            color="error"
            variant="contained"
            loading={isDeleting}
            loadingPosition="start"
            sx={{ textTransform: "none" }}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
